// 🏗 [건설] 사고 사례 브리핑 장표 3종 — 사고 개요 패널 · 예방대책 카드 · TBM 카드(2026-10-09 · 장표 본부).
//   정답지 = 이치죠 «굴착기 버킷 낙하 v1»(72.6초 · 회사 틀 Edu-B). 수치는 `construction-slide-tokens.ts` 한 곳.
//   ★자기 패널 장표★ — 다른 장표 10종은 장표 층이 화면 전체 딤·VM 축소 상자를 씌우지만, 이 셋은 자기가 바탕을 정한다:
//     사고 개요·예방대책 = 바탕(영상·그림)은 왼쪽에 또렷이 두고 오른쪽만 어둡게 + 패널 / TBM = 자기 어두운 바탕 + 크림 카드.
//     그래서 장표 층(`Overlay169SlideLayer`)은 이 셋을 딤·쓸림·돌진 없이 화면 전체에 그대로 얹는다(`SELF_PANEL_SLIDE_TYPES`).
//   ★받은 글자를 바꾸지 않는다★(원문 고정 · verbatim-lock) — 자르기·말줄임·생략 0. 칸에 안 들어가면 덩어리째 줄인다(하한 0.55).
//     숫자+단위 덩이는 한 상자(`NumRunText` · «2026. 10. / 2.» 방지). 빈 칸은 줄째 빠지고 등장도 당겨진다.
//   ★자막과 겹치지 않는다★ — 호스트가 자막 윗선(`captionTopPx`)을 주면 그 위에서 끝나게 위로 올리고, 그래도 넘치면 줄인다.
//   내보내기 엔진이 그리는 것만 쓴다: linear-gradient 한 장 · 2D 변환 · 둥근 모서리 · 인라인 svg(줄무늬·체크 획).
import React from 'react';
import { AbsoluteFill, useVideoConfig } from 'remotion';
import { NumRunText, itemEnterFrames, type LayoutBaseProps } from './layouts';
import { KO_TEXT, FW } from './tokens';
import { briefLineCount } from './motion-brief-shape';
import { safeInsetPx } from '../../lib/safe-zone';
import {
  CB_CAPTION_GAP, CB_MIN_SCALE, CB_PANEL, CB_TBM, CB_TIMING, CONSTRUCTION_COLORS as CC,
} from './construction-slide-tokens';
import {
  INCIDENT_ROW_KEYS, INCIDENT_ROW_LABELS, CONSTRUCTION_SLIDE_DEFAULTS,
  type IncidentBriefContent, type PreventionCardContent, type TbmCardContent,
} from '../../lib/templates/slide-kind-contract';

/** 장표 층이 렌더 경계에서만 주는 값 — `slide.content` 에 싣지 않는다(beats 와 같은 이유). */
export interface ConstructionSlideExtra {
  /** 자막 막대 윗선(화면 px). 없으면 세이프존 아래 끝까지 쓴다. */
  captionTopPx?: number;
  /** 🧑‍🏫 오른쪽 프레젠터 자리(화면 px) — 있으면 패널은 왼쪽으로(VM 한쪽 패널과 같은 규칙) · TBM 카드는 그만큼 좁아진다. */
  reserveRightPx?: number;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : []);
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
/** 정답지 ease — 3제곱 감속. */
const ease = (x: number) => 1 - Math.pow(1 - clamp01(x), 3);
/** 정답지 ap(t, t0, d) 의 프레임판. */
const ap = (f: number, at: number, dur: number) => ease((f - at) / Math.max(1, dur));
/** 줄 수 어림 여유 — 어림이 실제보다 짧을 때 넘치지 않게(4%). */
const WIDTH_SLACK = 1.04;
const lines = (text: string, px: number, w: number) => Math.max(1, briefLineCount(text, px, w / WIDTH_SLACK));

/** 화면 좌표계 — 1920 기준 × (화면 폭 ÷ 1920). ★16:9 전용★: 장표(StorySlide)는 16:9 설계도 층(Overlay169)만 그린다
 *  (세로 스토리 템플릿은 장표를 그리지 않는다 · 정밀 편집도 세로 장표는 비활성) — 세로 배치를 따로 두지 않는다. */
function useBase() {
  const { width, height, fps } = useVideoConfig();
  const u = width / 1920;
  const safe = safeInsetPx('16:9', width, height, 'core');
  return { width, height, fps, u, safe };
}

/** 등장 시각 — 낱말 신호(그 항목을 말하는 순간)가 있으면 그 시각, 없으면 정답지 간격(첫 1.0초 · 0.55초씩). */
function revealFrames(count: number, fps: number, p: Pick<LayoutBaseProps, 'itemCues' | 'beats'>, firstSec: number, gapSec: number): number[] {
  const fixed = Array.from({ length: count }, (_, j) => Math.round((firstSec + j * gapSec) * fps));
  const cues = p.itemCues;
  if (!cues || cues.length !== count || !cues.some((v) => typeof v === 'number' && Number.isFinite(v))) return fixed;
  return itemEnterFrames(count, { cues, beats: p.beats, revealMode: 'sequential', base: fixed[0] });
}

/** 덩어리 높이 h(화면 px)를 [위 선호값, 아래 한계] 에 앉힌다 — 넘치면 위로 올리고(세이프존 위까지), 그래도 넘치면 줄인다. */
function seat(h: number, prefTop: number, minTop: number, bottom: number): { top: number; k: number } {
  if (prefTop + h <= bottom) return { top: prefTop, k: 1 };
  const top = Math.max(minTop, bottom - h);
  if (top + h <= bottom) return { top, k: 1 };
  return { top: minTop, k: Math.max(CB_MIN_SCALE, (bottom - minTop) / h) };
}

/** 꼬리표(빨강·노랑) — 정답지 .tag (모서리만 8px). */
const Tag: React.FC<{ text: string; bg: string; fg: string; u: number }> = ({ text, bg, fg, u }) => (
  <div data-cb-tag="" style={{ display: 'inline-block', alignSelf: 'flex-start', padding: `${CB_PANEL.tagPadY * u}px ${CB_PANEL.tagPadX * u}px`,
    borderRadius: CB_PANEL.tagRadius * u, background: bg, color: fg, fontSize: CB_PANEL.tagPx * u, fontWeight: FW.bold, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
    {text}
  </div>
);

/** 사고 개요·예방대책 공통 — 바탕 위 오른쪽 어둠 + 패널. */
const SidePanel: React.FC<LayoutBaseProps & ConstructionSlideExtra & {
  tag: string; tagBg: string; tagFg: string; title: string;
  /** 줄 높이 어림에 쓰는 본문 줄 목록 — 렌더 순서와 같다. */
  measure: (colW: number) => number;
  children: (reveal: (j: number) => React.CSSProperties) => React.ReactNode;
  count: number; cueable: boolean;
}> = (p) => {
  const b = useBase();
  const { f } = p;
  const fps = b.fps;
  // 1) 자리 — 정답지 #panel(x 1180 · 폭 680 · 위 200) · 오른쪽은 세이프존 안.
  //    프레젠터가 오른쪽을 쓰면 패널은 왼쪽(세이프존 왼쪽부터 같은 폭) · 어둠도 왼쪽.
  const onLeft = (p.reserveRightPx ?? 0) > 0;
  const right = onLeft ? b.safe.left + (1920 - CB_PANEL.left) * b.u - b.safe.right : b.width - b.safe.right;
  const left = onLeft ? b.safe.left : Math.min(CB_PANEL.left * b.u, right - 320 * b.u);
  const colW = right - left;
  const tagH = (CB_PANEL.tagPx * 1.2 + 2 * CB_PANEL.tagPadY) * b.u;
  const titlePx = CB_PANEL.titlePx * b.u;
  // 제목이 비면(브리핑 사실로 조립했는데 디렉터 장면 제목이 없을 때) 제목 줄째 빠지고 꼬리표 바로 아래 본문.
  const hasTitle = p.title.trim() !== '';
  const titleH = hasTitle ? lines(p.title, titlePx, colW) * titlePx * CB_PANEL.titleLh + (CB_PANEL.titleGapTop + CB_PANEL.titleGapBottom) * b.u : CB_PANEL.titleGapBottom * b.u;
  const h = tagH + titleH + p.measure(colW);
  const capTop = typeof p.captionTopPx === 'number' && Number.isFinite(p.captionTopPx) && p.captionTopPx > 0 ? p.captionTopPx : undefined;
  const bottom = Math.min(b.height - b.safe.bottom, capTop !== undefined ? capTop - CB_CAPTION_GAP * b.u : Infinity);
  const { top, k } = seat(h, CB_PANEL.top * b.u, b.safe.top, bottom);
  // 2) 박자 — 어둠 0.2초(0.8초) · 패널 0.4초(0.6초 · 30px 밀려 들어옴) · 줄 1.0초 + 0.55초씩.
  const sh = ap(f, CB_TIMING.scrimAt * fps, CB_TIMING.scrimDur * fps);
  const pa = ap(f, CB_TIMING.panelAt * fps, CB_TIMING.panelDur * fps);
  const at = p.cueable
    ? revealFrames(p.count, fps, p, CB_TIMING.itemFirst, CB_TIMING.itemGap)
    : Array.from({ length: p.count }, (_, j) => Math.round((CB_TIMING.itemFirst + j * CB_TIMING.itemGap) * fps));
  const reveal = (j: number): React.CSSProperties => {
    const a = ap(f, at[j] ?? 0, CB_TIMING.itemDur * fps);
    return { opacity: a, transform: `translateX(${(1 - a) * CB_TIMING.itemShift * b.u}px)` };
  };
  const [r, g, bl] = CC.scrimRgb;
  const S = CB_PANEL.scrim;
  const scrim = `linear-gradient(to ${onLeft ? 'left' : 'right'}, rgba(${r},${g},${bl},0) ${S.clearAt}%, rgba(${r},${g},${bl},${S.mid * sh}) ${S.midAt}%, rgba(${r},${g},${bl},${S.deep * sh}) 100%)`;
  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      <AbsoluteFill data-cb-scrim="" style={{ background: scrim }} />
      <div data-cb-panel="" style={{ position: 'absolute', left, top, width: colW, transformOrigin: '0 0',
        transform: `${k < 1 ? `scale(${k}) ` : ''}translateX(${(1 - pa) * CB_TIMING.panelShift * b.u}px)`, opacity: pa,
        display: 'flex', flexDirection: 'column', color: '#ffffff' }}>
        <Tag text={p.tag} bg={p.tagBg} fg={p.tagFg} u={b.u} />
        {hasTitle ? (
          <div data-cb-title="" style={{ fontSize: titlePx, fontWeight: FW.extrabold, lineHeight: CB_PANEL.titleLh, letterSpacing: -1 * b.u,
            marginTop: CB_PANEL.titleGapTop * b.u, marginBottom: CB_PANEL.titleGapBottom * b.u, ...KO_TEXT }}>
            <NumRunText text={p.title} />
          </div>
        ) : <div style={{ height: CB_PANEL.titleGapBottom * b.u }} />}
        {p.children(reveal)}
      </div>
    </AbsoluteFill>
  );
};

// ─── ① 사고 개요 패널 ────────────────────────────────────────────────

export const IncidentBrief: React.FC<LayoutBaseProps & ConstructionSlideExtra & IncidentBriefContent> = (p) => {
  const b = useBase();
  const rows = INCIDENT_ROW_KEYS.map((key) => ({ key, label: INCIDENT_ROW_LABELS[key], value: str(p[key]) })).filter((r) => r.value.trim() !== '');
  const rowPx = CB_PANEL.rowPx * b.u, labelW = CB_PANEL.rowLabelW * b.u, gap = CB_PANEL.rowGap * b.u;
  const measure = (colW: number) => rows.reduce((h, r, j) =>
    h + (j ? gap : 0) + Math.max(lines(r.label, rowPx, labelW), lines(r.value, rowPx, colW - labelW)) * rowPx * CB_PANEL.rowLh, 0);
  return (
    <SidePanel {...p} tag={str(p.tag) || CONSTRUCTION_SLIDE_DEFAULTS.incidentBrief.tag} tagBg={CC.red} tagFg="#ffffff" title={str(p.title)}
      measure={measure} count={rows.length} cueable={false}>
      {(reveal) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>
          {rows.map((r, j) => (
            <div key={r.key} data-cb-row={r.key} style={{ display: 'flex', fontSize: rowPx, lineHeight: CB_PANEL.rowLh, ...reveal(j) }}>
              <div style={{ width: labelW, flex: 'none', color: CC.red, fontWeight: FW.bold, ...KO_TEXT }}>{r.label}</div>
              <div style={{ flex: 1, minWidth: 0, fontWeight: FW.medium, ...KO_TEXT }}><NumRunText text={r.value} /></div>
            </div>
          ))}
        </div>
      )}
    </SidePanel>
  );
};

// ─── ② 예방대책 카드 ─────────────────────────────────────────────────

/** 노란 네모 점이 붙은 줄 — 예방대책 점 목록과 TBM 질문이 같은 모양을 쓴다(크기만 다르다). */
const DotLine: React.FC<{ text: string; px: number; lh: number; indent: number; dot: number; dotTop: number; weight: number; style?: React.CSSProperties; attr: string }> = (d) => (
  <div data-cb-item={d.attr} style={{ position: 'relative', paddingLeft: d.indent, fontSize: d.px, fontWeight: d.weight, lineHeight: d.lh, ...KO_TEXT, ...d.style }}>
    <div style={{ position: 'absolute', left: d.dot * 0.36, top: d.dotTop, width: d.dot, height: d.dot, borderRadius: d.dot * 0.2, background: CC.yellow }} />
    <NumRunText text={d.text} />
  </div>
);

export const PreventionCard: React.FC<LayoutBaseProps & ConstructionSlideExtra & PreventionCardContent> = (p) => {
  const b = useBase();
  const items = strs(p.preventions);
  const px = CB_PANEL.bulPx * b.u, gap = CB_PANEL.bulGap * b.u, indent = CB_PANEL.bulIndent * b.u;
  const measure = (colW: number) => items.reduce((h, t, j) => h + (j ? gap : 0) + lines(t, px, colW - indent) * px * CB_PANEL.bulLh, 0);
  return (
    <SidePanel {...p} tag={str(p.tag) || CONSTRUCTION_SLIDE_DEFAULTS.preventionCard.tag} tagBg={CC.yellow} tagFg={CC.ink} title={str(p.title)}
      measure={measure} count={items.length} cueable>
      {(reveal) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>
          {items.map((t, j) => (
            <DotLine key={j} attr={String(j)} text={t} px={px} lh={CB_PANEL.bulLh} indent={indent} dot={CB_PANEL.bulDot * b.u} dotTop={CB_PANEL.bulDotTop * b.u}
              weight={FW.semibold} style={reveal(j)} />
          ))}
        </div>
      )}
    </SidePanel>
  );
};

// ─── ③ TBM 카드 ──────────────────────────────────────────────────────

/** 정답지 체크 획(M18 52 L42 74 L84 28 · 길이 ≈ 95 → 대시 110). */
const CHECK_PATH = 'M18 52 L42 74 L84 28';
const CHECK_DASH = 110;

export const TbmCard: React.FC<LayoutBaseProps & ConstructionSlideExtra & TbmCardContent> = (p) => {
  const b = useBase();
  const { f } = p;
  const fps = b.fps;
  const checks = strs(p.checks);
  const tag = str(p.tag) || CONSTRUCTION_SLIDE_DEFAULTS.tbmCard.tag;
  const title = str(p.title) || CONSTRUCTION_SLIDE_DEFAULTS.tbmCard.title;
  const source = str(p.source);
  const T = CB_TBM, u = b.u;
  // 1) 자리 — 정답지 카드(150,150 · 1620×610) · 체크 칸은 카드 오른쪽 위. 프레젠터가 오른쪽을 쓰면 카드가 그만큼 좁아진다.
  const cardLeft = Math.max(b.safe.left, T.cardLeft * u);
  const cardW = Math.min(T.cardW * u, b.width - b.safe.right - (p.reserveRightPx ?? 0) - cardLeft);
  const padX = T.padX * u, padY = T.padY * u;
  const boxSize = T.boxSize * u, boxRight = T.boxRight * u, boxTop = T.boxTop * u;
  // 글 칸 폭 — 체크 칸과 겹치지 않게(16:9 정답지: 카드 폭 − 왼쪽 안쪽 − (체크 칸 오른쪽 + 칸 + 40)).
  const textW = cardW - padX - (boxRight + boxSize + 40 * u);
  const tagH = (CB_PANEL.tagPx * 1.2 + 2 * CB_PANEL.tagPadY) * u;
  const titlePx = T.titlePx * u, qPx = T.qPx * u, srcPx = T.srcPx * u, qGap = T.qGap * u, qIndent = T.qIndent * u;
  const titleH = lines(title, titlePx, textW) * titlePx * 1.2;
  const qH = checks.reduce((h, t, j) => h + (j ? qGap : 0) + lines(t, qPx, textW - qIndent) * qPx * T.qLh, 0);
  const srcH = source ? lines(source, srcPx, cardW - 2 * padX) * srcPx * 1.35 : 0;
  const bodyH = padY + tagH + T.titleGapTop * u + titleH + T.titleGapBottom * u + qH + (source ? 30 * u + srcH + T.srcBottom * u : padY);
  const cardH = Math.max(T.cardH * u, bodyH, boxTop + boxSize + padY);
  const capTop = typeof p.captionTopPx === 'number' && Number.isFinite(p.captionTopPx) && p.captionTopPx > 0 ? p.captionTopPx : undefined;
  const bottom = Math.min(b.height - b.safe.bottom, capTop !== undefined ? capTop - CB_CAPTION_GAP * u : Infinity);
  const minTop = Math.max(b.safe.top, (T.stripeH + 24) * u);
  const { top, k } = seat(cardH, Math.max(minTop, T.cardTop * u), minTop, bottom);
  // 2) 박자 — 카드 0.1초(0.5초 · 30px 올라옴) · 질문 = 말하는 순간(낱말 신호) 또는 나레이션 시작 + 3.2초 + 2.4초씩 · 체크 = 마지막 질문 + 2.0초.
  const ca = ap(f, CB_TIMING.cardAt * fps, CB_TIMING.cardDur * fps);
  const n0 = typeof p.beats?.[0]?.atFrame === 'number' && Number.isFinite(p.beats[0].atFrame) ? p.beats[0].atFrame / fps : CB_TIMING.narrationStart;
  const sceneEnd = p.beats?.length ? p.beats[p.beats.length - 1]?.untilFrame : undefined;
  let qAt = revealFrames(checks.length, fps, p, n0 + CB_TIMING.qFirst, CB_TIMING.qGap);
  // 장면이 짧으면(마지막 질문 + 체크가 장면 끝을 넘으면) 질문을 [나레이션 시작 + 1초, 끝 − 2.6초] 에 고르게 편다 — 낱말 신호가 없을 때만.
  const usedCues = !!p.itemCues && p.itemCues.length === checks.length && p.itemCues.some((v) => typeof v === 'number');
  if (!usedCues && typeof sceneEnd === 'number' && checks.length && qAt[qAt.length - 1] + (CB_TIMING.checkAfter + CB_TIMING.checkDur) * fps > sceneEnd) {
    const lo = (n0 + 1.0) * fps, hi = Math.max(lo, sceneEnd - (CB_TIMING.checkAfter + CB_TIMING.checkDur) * fps);
    qAt = checks.map((_, j) => Math.round(checks.length > 1 ? lo + ((hi - lo) * j) / (checks.length - 1) : lo));
  }
  const lastQ = qAt.length ? qAt[qAt.length - 1] : Math.round(n0 * fps);
  let checkAt = lastQ + Math.round(CB_TIMING.checkAfter * fps);
  if (typeof sceneEnd === 'number') checkAt = Math.max(lastQ + Math.round(0.4 * fps), Math.min(checkAt, sceneEnd - Math.round((CB_TIMING.checkDur + 0.2) * fps)));
  const ck = ap(f, checkAt, CB_TIMING.checkDur * fps);
  // 줄무늬 — 노랑·검정 45° 띠(정답지 repeating-linear-gradient). 내보내기 엔진은 repeating- 을 못 그려 인라인 svg 무늬로 그린다.
  const stripeH = T.stripeH * u, stripeW = T.stripeW * u;
  return (
    <AbsoluteFill style={{ pointerEvents: 'none', background: CC.dark }}>
      <svg data-cb-stripe="" aria-hidden width={b.width} height={stripeH} style={{ position: 'absolute', left: 0, top: 0 }}>
        <defs>
          <pattern id="cb-tbm-stripe" patternUnits="userSpaceOnUse" width={stripeW * 2} height={stripeW * 2} patternTransform="rotate(45)">
            <rect x={0} y={0} width={stripeW} height={stripeW * 2} fill={CC.yellow} />
          </pattern>
        </defs>
        <rect x={0} y={0} width={b.width} height={stripeH} fill={CC.dark} />
        <rect x={0} y={0} width={b.width} height={stripeH} fill="url(#cb-tbm-stripe)" />
      </svg>
      <div data-cb-card="" style={{ position: 'absolute', left: cardLeft, top, width: cardW, height: cardH, boxSizing: 'border-box',
        transformOrigin: '50% 0', transform: `${k < 1 ? `scale(${k}) ` : ''}translateY(${(1 - ca) * CB_TIMING.cardShift * u}px)`, opacity: ca,
        background: CC.cream, border: `${T.border * u}px solid ${CC.yellow}`, borderRadius: T.radius * u, color: CC.ink,
        padding: `${padY}px ${padX}px`, display: 'flex', flexDirection: 'column' }}>
        <Tag text={tag} bg={CC.yellow} fg={CC.ink} u={u} />
        <div data-cb-title="" style={{ fontSize: titlePx, fontWeight: FW.extrabold, lineHeight: 1.2, letterSpacing: -1 * u,
          marginTop: T.titleGapTop * u, marginBottom: T.titleGapBottom * u, maxWidth: textW, ...KO_TEXT }}>
          <NumRunText text={title} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: qGap, maxWidth: textW }}>
          {checks.map((t, j) => (
            <DotLine key={j} attr={String(j)} text={t} px={qPx} lh={T.qLh} indent={qIndent} dot={T.qDot * u} dotTop={T.qDotTop * u}
              weight={FW.bold} style={{ opacity: ap(f, qAt[j] ?? 0, CB_TIMING.qDur * fps) }} />
          ))}
        </div>
        {source ? (
          <div data-cb-source="" style={{ position: 'absolute', left: padX, right: padX, bottom: T.srcBottom * u, fontSize: srcPx, lineHeight: 1.35, color: CC.source, ...KO_TEXT }}>
            <NumRunText text={source} />
          </div>
        ) : null}
        <div data-cb-box="" style={{ position: 'absolute', right: boxRight, top: boxTop, width: boxSize, height: boxSize, boxSizing: 'border-box',
          border: `${T.boxBorder * u}px solid ${CC.yellow}`, borderRadius: T.boxRadius * u, background: CC.boxFill,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg viewBox="0 0 100 100" width={boxSize * (T.checkSize / T.boxSize)} height={boxSize * (T.checkSize / T.boxSize)}>
            <path d={CHECK_PATH} fill="none" stroke={CC.ink} strokeWidth={T.checkStroke} strokeLinecap="round" strokeLinejoin="round"
              strokeDasharray={CHECK_DASH} strokeDashoffset={CHECK_DASH * (1 - ck)} />
          </svg>
        </div>
      </div>
    </AbsoluteFill>
  );
};

/** 장표 층이 «자기 패널»로 그리는 종류 — 딤·VM 축소 상자·쓸림·돌진·모서리 접힘 없이 화면 전체에 얹는다. */
export const CONSTRUCTION_SLIDE_COMPONENTS = {
  incidentBrief: IncidentBrief,
  preventionCard: PreventionCard,
  tbmCard: TbmCard,
} as const;
